import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, Scissors, Printer, Shirt, CheckCircle2, Flame,
  Truck, Database, Factory, Boxes, LogOut, KeyRound, FileBarChart2, Layers, Package, ScanLine, Trophy,
  Menu, X, Activity, Trash2, Image as ImageIcon, Undo2, GitBranch, Briefcase
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/masters", icon: Database, label: "Masters", testid: "nav-masters" },
  { to: "/receipts", icon: Factory, label: "Fabric Receipts", testid: "nav-receipts" },
  { to: "/rolls", icon: Package, label: "Rolls", testid: "nav-rolls" },
  { to: "/pipeline", icon: GitBranch, label: "Pipeline", testid: "nav-pipeline" },
  { to: "/job-work", icon: Briefcase, label: "Job Work", testid: "nav-job-work" },
  { to: "/returns", icon: Undo2, label: "Returns", testid: "nav-returns" },
  { to: "/scan", icon: ScanLine, label: "Scan Roll", testid: "nav-scan" },
  { to: "/cutting-orders", icon: Scissors, label: "Cutting Orders", testid: "nav-cutting-orders" },
  { to: "/cutting", icon: Scissors, label: "Cutting", testid: "nav-cutting" },
  { to: "/batches", icon: Layers, label: "Batches", testid: "nav-batches" },
  { to: "/designs", icon: ImageIcon, label: "Designs", testid: "nav-designs" },
  { to: "/printing", icon: Printer, label: "Printing", testid: "nav-printing" },
  { to: "/sewing", icon: Shirt, label: "Sewing", testid: "nav-sewing" },
  { to: "/qc", icon: CheckCircle2, label: "QC", testid: "nav-qc" },
  { to: "/ironing", icon: Flame, label: "Ironing", testid: "nav-ironing" },
  { to: "/finished-stock", icon: Boxes, label: "Finished Stock", testid: "nav-finished-stock" },
  { to: "/dispatch", icon: Truck, label: "Dispatch", testid: "nav-dispatch" },
  { to: "/reports", icon: FileBarChart2, label: "Reports", testid: "nav-reports" },
  { to: "/vendor-performance", icon: Trophy, label: "Vendor Scores", testid: "nav-vendor-perf" },
  { to: "/activity", icon: Activity, label: "Activity Log", testid: "nav-activity" },
  { to: "/unicommerce", icon: Truck, label: "Unicommerce", testid: "nav-unicommerce", admin: true },
  { to: "/trash", icon: Trash2, label: "Trash", testid: "nav-trash", admin: true },
];

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const visibleNav = NAV.filter((it) => {
    if (it.admin && !hasRole("admin")) return false;
    if (it.roles) return it.roles.some((r) => hasRole(r));
    return true;
  });

  const Sidebar = ({ onNavigate }) => (
    <div className="w-56 shrink-0 bg-white border-r border-zinc-200 flex flex-col h-full" data-testid="sidebar">
      <div className="p-4 border-b border-zinc-200 flex items-center gap-2.5">
        <img src="/durakart-logo.png" alt="Durakart" className="w-8 h-8 object-contain" />
        <div>
          <div className="font-heading text-base font-black tracking-tighter text-zinc-950 leading-none">BE AWARA</div>
          <div className="data-label mt-1 text-[9px]">Production OS</div>
        </div>
      </div>
      <nav className="flex-1 py-3 scrollbar-thin overflow-y-auto">
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors border-l-2 ${
                isActive
                  ? "bg-zinc-50 text-zinc-950 border-zinc-950 font-semibold"
                  : "text-zinc-600 border-transparent hover:bg-zinc-50 hover:text-zinc-950"
              }`
            }
          >
            <item.icon size={16} strokeWidth={1.75} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-zinc-200">
        <div className="text-xs text-zinc-600 mb-2" data-testid="current-user">
          <div className="font-semibold text-zinc-950">{user?.name}</div>
          <div className="flex flex-wrap gap-1 mt-1" data-testid="current-user-roles">
            {(user?.roles || [user?.role]).filter(Boolean).map((r) => (
              <span key={r} className="font-mono text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded-sm">{r}</span>
            ))}
          </div>
        </div>
        <button
          onClick={() => { nav("/account/password"); }}
          data-testid="change-password-link"
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-700 hover:bg-zinc-100 border border-zinc-200 rounded-sm transition-colors mb-2"
        >
          <KeyRound size={14} /> Change Password
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-700 hover:bg-zinc-100 border border-zinc-200 rounded-sm transition-colors"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="w-56 bg-white h-full shadow-xl">
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 sticky top-0 z-30">
          <button onClick={() => setOpen(true)} className="p-1.5 rounded-sm hover:bg-zinc-100" data-testid="mobile-menu-button" aria-label="Open menu">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <img src="/durakart-logo.png" alt="Durakart" className="w-6 h-6 object-contain" />
            <div className="font-heading text-sm font-black tracking-tighter text-zinc-950">BE AWARA</div>
          </div>
          <button onClick={handleLogout} className="p-1.5 rounded-sm hover:bg-zinc-100" data-testid="mobile-logout" aria-label="Sign out">
            <LogOut size={18} />
          </button>
        </div>

        {/* close icon inside drawer header for clarity */}
        {open && (
          <button onClick={() => setOpen(false)} className="md:hidden fixed top-3 right-4 z-50 text-white p-1" data-testid="mobile-drawer-close" aria-label="Close menu">
            <X size={22} />
          </button>
        )}

        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
